import React, { useState, useRef, useEffect } from "react";
import "../../styles/Navbar/Navbar.css";
import { useNavigate, Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";

const navItems = [
  {
    label: "Programs",
    children: [
      "Education Initiatives",
      "Health & Wellness",
      "Community Development",
      "Scholarships",
    ],
  },
  {
    label: "Impact",
    children: ["Success Stories", "Testimonials"],
  },
  {
    label: "Partnerships",
    children: [
      "Our Partners",
      "Become a Partner",
      "Corporate Sponsorships",
      "Volunteer",
    ],
  },
  {
    label: "News & Events",
    children: ["Upcoming Events"],
  },
];

export default function Navbar() {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(null);
  const navigate = useNavigate();
  const navRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  const navigateToDonate = () => {
    navigate("/donate");
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
        setMobileDropdownOpen(null);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      // Clear any pending timeout on cleanup
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, [mobileMenuOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e, index) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (window.innerWidth <= 767) {
        // Mobile: toggle dropdown
        setMobileDropdownOpen(mobileDropdownOpen === index ? null : index);
      } else {
        // Desktop: open dropdown
        setOpenMenu(index);
      }
    } else if (e.key === "Escape") {
      setOpenMenu(null);
      setMobileDropdownOpen(null);
    }
  };

  const toggleMobileDropdown = (index) => {
    setMobileDropdownOpen(mobileDropdownOpen === index ? null : index);
  };

  const handleMouseEnter = (index) => {
    if (window.innerWidth > 767) {
      // Clear any pending close timeout
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setOpenMenu(index);
    }
  };

  const handleMouseLeave = () => {
    if (window.innerWidth > 767) {
      // Add a small delay before closing
      closeTimeoutRef.current = setTimeout(() => {
        setOpenMenu(null);
      }, 150);
    }
  };

  return (
    <nav className="navbar" ref={navRef}>
      <Link to="/" className="logo" aria-label="PhabyéonJ'n-Pierre Foundation Home">
        <span className="text-blue">PhabyéonJ'n-Pierre</span>
        <span className="text-green"> Foundation</span>
      </Link>
      <button
        className="hamburger"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle navigation menu"
        aria-expanded={mobileMenuOpen}
      >
        {mobileMenuOpen ? "✖" : "☰"}
      </button>
      <div className={`nav-links ${mobileMenuOpen ? "show" : ""}`}>
        <Link to="/">Home</Link>
        <a href="#about">About Us</a>
        {navItems.map((item, index) => (
          <div
            className="dropdown"
            key={item.label}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className="dropbtn"
              onClick={() => window.innerWidth <= 767 && toggleMobileDropdown(index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              aria-haspopup="true"
              aria-expanded={
                window.innerWidth <= 767
                  ? mobileDropdownOpen === index
                  : openMenu === index
              }
            >
              {item.label}
              <ChevronDown
                className="dropdown-icon"
                size={16}
                style={{
                  marginLeft: "0.25rem",
                  transition: "transform 0.3s ease",
                  transform:
                    (window.innerWidth <= 767 && mobileDropdownOpen === index) ||
                    (window.innerWidth > 767 && openMenu === index)
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                }}
              />
            </button>
            <div
              className="dropdown-content"
              style={{
                display:
                  (window.innerWidth <= 767 && mobileDropdownOpen === index) ||
                  (window.innerWidth > 767 && openMenu === index)
                    ? "block"
                    : "none",
              }}
            >
              {item.children.map((child) => {
                if (child === "Upcoming Events") {
                  return (
                    <Link key={child} to="/upcoming-events">
                      {child}
                    </Link>
                  );
                }
                return (
                  <a key={child} href="/">
                    {child}
                  </a>
                );
              })}
            </div>
          </div>
        ))}
        <Link to="/faq">FAQ</Link>
        <a href="#contact">Contact</a>
        <button className="donate-button" onClick={() => navigateToDonate()}>
          Donate
        </button>
      </div>
    </nav>
  );
}
